/**
 * Visa document-translation comparison cluster — /visa/compare/{slug}.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - นิติกรณ์: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ประเทศไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2027
 * - VFS/TLScontact/JVAC/CVASC/KVAC เป็นศูนย์รับคำร้องแทนสถานทูต ไม่ใช่ผู้ออกวีซ่า
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */

export interface VisaCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface VisaCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: VisaCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — เงื่อนไขของสถานทูตและหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const VISA_COMPARE: VisaCompare[] = [
  {
    slug: "แปลรับรองผู้แปล-vs-นิติกรณ์กงสุล",
    title: "คำแปลรับรองโดยผู้แปล กับ คำแปลผ่านนิติกรณ์กรมการกงสุล",
    description:
      "เทียบทีละข้อว่าเอกสารยื่นวีซ่าของคุณใช้คำแปลพร้อมคำรับรองผู้แปลได้เลย หรือต้องผ่านนิติกรณ์กรมการกงสุลก่อน พร้อมค่าธรรมเนียมราชการและกรอบเวลาโดยประมาณ",
    h1: "คำแปลรับรองโดยผู้แปล vs คำแปลผ่านนิติกรณ์กรมการกงสุล",
    aLabel: "คำรับรองผู้แปล",
    bLabel: "นิติกรณ์กรมการกงสุล",
    answer:
      "ถ้าเช็กลิสต์ของสถานทูตเขียนเพียงว่า certified translation หรือ translation by a professional translator คำแปลพร้อมคำรับรองผู้แปลมักเพียงพอ แต่ถ้าเขียนว่า legalised หรือ authenticated by the Ministry of Foreign Affairs ต้องเข้านิติกรณ์กรมการกงสุลก่อนเสมอ",
    rows: [
      { aspect: "ผู้รับรอง", a: "ผู้แปลหรือหน่วยงานผู้แปล", b: "กองสัญชาติและนิติกรณ์ กรมการกงสุล" },
      { aspect: "สิ่งที่รับรอง", a: "ความถูกต้องของคำแปล", b: "ความถูกต้องของคำแปลเทียบกับต้นฉบับราชการ" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "ไม่มี", b: "200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน)" },
      { aspect: "ต้องใช้ต้นฉบับตัวจริง", a: "ไม่จำเป็นเสมอไป", b: "ต้องใช้ต้นฉบับหรือฉบับคัดรับรองตัวจริง" },
      { aspect: "กรอบเวลาโดยประมาณ", a: "1-3 วันทำการ", b: "เพิ่มอีกประมาณ 2-3 วันทำการ (ด่วนเร็วกว่า)" },
      { aspect: "ปลายทางที่มักกำหนด", a: "อังกฤษ ออสเตรเลีย แคนาดา นิวซีแลนด์", b: "กลุ่มเชงเก้น ตะวันออกกลาง จีน เกาหลี ญี่ปุ่นบางกรณี" },
    ],
    chooseA: [
      "เช็กลิสต์ระบุเพียง certified translation",
      "ยื่นเอกสารสนับสนุนที่ไม่ใช่ทะเบียนราษฎร",
      "ต้องการกรอบเวลาสั้นที่สุด",
    ],
    chooseB: [
      "เช็กลิสต์ระบุ legalisation หรือ MFA authentication",
      "เป็นเอกสารทะเบียนราษฎร เช่น ทะเบียนบ้าน สูติบัตร ทะเบียนสมรส",
      "ต้องนำไปรับรองที่สถานทูตปลายทางต่ออีกชั้น",
    ],
    faqs: [
      {
        q: "ถ้าไม่แน่ใจว่าปลายทางต้องการชั้นไหน ควรทำอย่างไร",
        a: "ยึดหน้าเว็บของสถานทูตปลายทางเป็นหลักฐานหลัก และส่งเช็กลิสต์มาให้ทีมงานตรวจก่อนเริ่มงาน จะได้ไม่ต้องแปลหรือรับรองซ้ำ " + TAIL,
      },
      {
        q: "รับรองผู้แปลแล้วยังนำไปเข้านิติกรณ์เพิ่มได้ไหม",
        a: "ได้ ตราบใดที่คำแปลยังตรงกับต้นฉบับและมีต้นฉบับตัวจริงประกอบ แต่ถ้าต้องแก้ข้อความในคำแปล ต้องแก้ให้เสร็จก่อนเข้าขั้นรับรองทุกครั้ง",
      },
      {
        q: "ค่าธรรมเนียมนิติกรณ์คิดอย่างไร",
        a: "คิดต่อตราประทับ 200 บาทสำหรับบริการปกติ และ 400 บาทสำหรับบริการด่วน ตามอัตราที่ประกาศบน consular.mfa.go.th ค่าธรรมเนียมราชการแสดงแยกจากค่าบริการของเราเสมอ",
      },
    ],
    synonyms: ["certified translation vs legalisation", "แปลรับรองกับนิติกรณ์ต่างกันยังไง", "ต้องรับรองกงสุลไหม"],
  },
  {
    slug: "แปลเอกสารเอง-vs-ให้สำนักงานแปล",
    title: "แปลเอกสารยื่นวีซ่าเอง กับ ให้สำนักงานที่รับงานรับรองดำเนินการ",
    description:
      "เทียบข้อดีข้อจำกัดของการแปลเอกสารวีซ่าด้วยตัวเอง กับการให้ทีมที่ทำงานรับรองเป็นประจำดำเนินการ ทั้งเรื่องการยอมรับของปลายทาง ความเสี่ยงถูกตีกลับ และเวลารวม",
    h1: "แปลเอกสารยื่นวีซ่าเอง vs ให้สำนักงานดำเนินการ",
    aLabel: "แปลเอง",
    bLabel: "ให้สำนักงานดำเนินการ",
    answer:
      "ถ้าปลายทางรับคำแปลจากผู้ยื่นเองและเอกสารมีไม่กี่ฉบับ การแปลเองประหยัดได้จริง แต่ถ้าเอกสารต้องผ่านนิติกรณ์หรือรับรองสถานทูต การให้ทีมที่เดินเรื่องเป็นประจำดำเนินการจะลดรอบแก้และลดความเสี่ยงถูกตีกลับ",
    rows: [
      { aspect: "การยอมรับของปลายทาง", a: "ขึ้นกับว่าสถานทูตยอมรับคำแปลจากผู้ยื่นหรือไม่", b: "จัดรูปแบบตามเช็กลิสต์ของปลายทางที่พบบ่อย" },
      { aspect: "ความเสี่ยงสะกดชื่อไม่ตรงพาสปอร์ต", a: "สูงถ้าไม่มีระบบตรวจเทียบ", b: "ตรวจเทียบกับหน้าพาสปอร์ตทุกฉบับก่อนรับรอง" },
      { aspect: "การเดินเรื่องนิติกรณ์", a: "ต้องไปยื่นเองที่กรมการกงสุล", b: "ทีมงานเดินเรื่องแทนได้" },
      { aspect: "รอบแก้ไข", a: "แก้แล้วต้องเริ่มขั้นรับรองใหม่", b: "ตรวจก่อนเข้าขั้นรับรองเพื่อลดการเริ่มใหม่" },
      { aspect: "ค่าใช้จ่าย", a: "เฉพาะค่าธรรมเนียมราชการ", b: "ค่าบริการสอบถามเป็นรายเคส + ค่าธรรมเนียมราชการแยกตามจริง" },
    ],
    chooseA: ["เอกสาร 1-2 ฉบับและปลายทางรับคำแปลจากผู้ยื่น", "มีเวลาไปยื่นและติดตามด้วยตนเอง"],
    chooseB: [
      "เอกสารหลายฉบับหรือหลายคนในชุดเดียว",
      "ต้องผ่านนิติกรณ์และรับรองสถานทูตต่อ",
      "มีกำหนดนัดสัมภาษณ์หรือวันยื่นที่ตายตัว",
    ],
    faqs: [
      {
        q: "แปลเองแล้วให้สำนักงานตรวจอย่างเดียวได้ไหม",
        a: "ได้ ทีมงานรับตรวจคำแปลที่ลูกค้าทำมาก่อนเข้าขั้นรับรอง สอบถามรายละเอียดและค่าบริการทางโทรศัพท์ LINE หรืออีเมล",
      },
      {
        q: "ใช้เครื่องมือแปลอัตโนมัติได้ไหม",
        a: "ไม่แนะนำสำหรับเอกสารราชการ เพราะเลขที่เอกสาร ชื่อหน่วยงาน และถ้อยคำทางกฎหมายมักคลาดเคลื่อน และเป็นสาเหตุถูกตีกลับที่พบบ่อย",
      },
      {
        q: "ต่างจังหวัดใช้บริการได้ไหม",
        a: "ได้ ส่งสแกนมาประเมินก่อน แล้วส่งต้นฉบับตัวจริงตามมาทางไปรษณีย์ลงทะเบียนหรือขนส่งเอกชน " + TAIL,
      },
    ],
    synonyms: ["แปลเอกสารวีซ่าเองได้ไหม", "diy visa translation", "จ้างแปลเอกสารวีซ่าคุ้มไหม"],
  },
  {
    slug: "นิติกรณ์กงสุล-vs-รับรองสถานทูตปลายทาง",
    title: "นิติกรณ์กรมการกงสุล กับ การรับรองโดยสถานทูตปลายทาง",
    description:
      "สองตราคนละหน้าที่ เทียบให้เห็นว่าตราไหนออกโดยใคร รับรองอะไร ต้องทำก่อนหลังอย่างไร และปลายทางกลุ่มไหนต้องการครบทั้งสองชั้น",
    h1: "นิติกรณ์กรมการกงสุล vs การรับรองโดยสถานทูตปลายทาง",
    aLabel: "นิติกรณ์กรมการกงสุล",
    bLabel: "รับรองโดยสถานทูตปลายทาง",
    answer:
      "นิติกรณ์กรมการกงสุลเป็นการรับรองของฝ่ายไทยว่าเอกสารและคำแปลถูกต้อง ส่วนการรับรองของสถานทูตปลายทางเป็นการยอมรับของประเทศผู้รับ ต้องทำนิติกรณ์ก่อนเสมอ แล้วจึงนำไปรับรองที่สถานทูต",
    rows: [
      { aspect: "ผู้ออกตรา", a: "กรมการกงสุล กระทรวงการต่างประเทศ", b: "สถานทูต/สถานกงสุลของประเทศปลายทางในไทย" },
      { aspect: "รับรองอะไร", a: "ลายมือชื่อเจ้าหน้าที่ผู้ออกเอกสารและความถูกต้องของคำแปล", b: "ยอมรับเอกสารเพื่อใช้ในประเทศของตน" },
      { aspect: "ลำดับ", a: "ทำก่อน", b: "ทำหลังนิติกรณ์" },
      { aspect: "ค่าธรรมเนียม", a: "200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน)", b: "อัตราของแต่ละสถานทูต ต่างกันรายประเทศ" },
      { aspect: "กรอบเวลาโดยประมาณ", a: "ประมาณ 2-3 วันทำการ (ด่วนเร็วกว่า)", b: "ขึ้นกับคิวของแต่ละสถานทูต" },
      { aspect: "จำเป็นเสมอไหม", a: "จำเป็นเมื่อปลายทางขอ legalisation", b: "เฉพาะบางประเทศที่กำหนดให้รับรองซ้ำ" },
    ],
    chooseA: ["ปลายทางขอเพียงตรารับรองของฝ่ายไทย", "เตรียมเอกสารไว้ล่วงหน้าก่อนรู้ปลายทางแน่ชัด"],
    chooseB: ["เช็กลิสต์ระบุ embassy legalisation หรือ consular attestation", "ปลายทางกลุ่มตะวันออกกลางและบางประเทศเชงเก้น"],
    faqs: [
      {
        q: "ข้ามขั้นนิติกรณ์ไปรับรองที่สถานทูตเลยได้ไหม",
        a: "โดยทั่วไปไม่ได้ เพราะสถานทูตส่วนใหญ่รับรองต่อจากตราของกรมการกงสุลเท่านั้น การข้ามขั้นมักทำให้ต้องกลับมาเริ่มใหม่ทั้งชุด",
      },
      {
        q: "ไทยออก Apostille แทนสองขั้นนี้ได้แล้วหรือยัง",
        a: "ยังไม่ได้ อนุสัญญาจะมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้ยังใช้เส้นทางนิติกรณ์แล้วรับรองสถานทูตตามเดิม",
      },
      {
        q: "เอกสารชุดเดิมใช้ยื่นหลายประเทศได้ไหม",
        a: "ตรานิติกรณ์ใช้ต่อยอดได้ แต่การรับรองของสถานทูตผูกกับประเทศนั้น ๆ หากยื่นหลายประเทศต้องเตรียมชุดแยกหรือรับรองเพิ่มตามจำนวนปลายทาง " + TAIL,
      },
    ],
    synonyms: ["นิติกรณ์กับรับรองสถานทูตต่างกันยังไง", "mfa vs embassy legalisation", "รับรองเอกสารสองชั้น"],
  },
  {
    slug: "แปลเอกสารวีซ่า-vs-แปลสาบานตนสถานทูต",
    title: "คำแปลยื่นวีซ่าทั่วไป กับ คำแปลจากผู้แปลที่สถานทูตขึ้นบัญชี",
    description:
      "บางสถานทูตกำหนดให้ใช้ผู้แปลในบัญชีของตนเท่านั้น เทียบให้ชัดว่าเมื่อไรใช้คำแปลทั่วไปได้ และเมื่อไรต้องใช้ผู้แปลที่สถานทูตยอมรับ",
    h1: "คำแปลยื่นวีซ่าทั่วไป vs ผู้แปลที่สถานทูตขึ้นบัญชี",
    aLabel: "คำแปลทั่วไป",
    bLabel: "ผู้แปลที่สถานทูตขึ้นบัญชี",
    answer:
      "ประเทศไทยไม่มีทะเบียนนักแปลสาบานตนกลางตามกฎหมาย ดังนั้นคำว่าแปลสาบานตนสำหรับเอกสารไทยหมายถึงผู้แปลที่สถานทูตปลายทางยอมรับหรือขึ้นบัญชีไว้ ต้องตรวจหน้าเว็บของสถานทูตก่อนเริ่มงานทุกครั้ง",
    rows: [
      { aspect: "ฐานที่มา", a: "ผู้แปลอาชีพทั่วไป", b: "รายชื่อผู้แปลที่สถานทูตประกาศหรือยอมรับ" },
      { aspect: "การยอมรับ", a: "ยอมรับกว้างในกลุ่มปลายทางที่ขอเพียง certified translation", b: "จำเป็นเมื่อสถานทูตระบุรายชื่อผู้แปลไว้" },
      { aspect: "ภาษาปลายทาง", a: "ส่วนใหญ่เป็นอังกฤษ", b: "มักเป็นภาษาราชการของประเทศนั้น" },
      { aspect: "ต้องผ่านนิติกรณ์ต่อ", a: "แล้วแต่ปลายทาง", b: "แล้วแต่ปลายทาง บางแห่งรับรองในตัวเอง" },
      { aspect: "ความเสี่ยงถูกตีกลับ", a: "สูงขึ้นถ้าปลายทางบังคับรายชื่อผู้แปล", b: "ต่ำเมื่อทำตามบัญชีที่ประกาศไว้" },
    ],
    chooseA: ["ปลายทางไม่ได้ระบุรายชื่อผู้แปล", "ยื่นกลุ่มอังกฤษ ออสเตรเลีย แคนาดา นิวซีแลนด์"],
    chooseB: ["สถานทูตประกาศรายชื่อผู้แปลที่ยอมรับ", "ต้องแปลเป็นภาษาราชการของประเทศปลายทาง"],
    faqs: [
      {
        q: "จะรู้ได้อย่างไรว่าสถานทูตบังคับใช้ผู้แปลในบัญชี",
        a: "อ่านหน้า legalisation, traduction หรือ document requirements บนเว็บไซต์ของสถานทูตปลายทาง ซึ่งเป็นหลักฐานหลักที่เจ้าหน้าที่หน้าเคาน์เตอร์ใช้ตรวจ",
      },
      {
        q: "ถ้าแปลผิดสายต้องทำใหม่ทั้งหมดไหม",
        a: "ส่วนใหญ่ต้องแปลใหม่ตามรูปแบบที่ปลายทางกำหนด และเสียค่าธรรมเนียมนิติกรณ์ซ้ำ จึงควรตรวจเช็กลิสต์ให้ชัดก่อนเริ่ม " + TAIL,
      },
      {
        q: "ทีมงานช่วยตรวจเช็กลิสต์ให้ก่อนได้ไหม",
        a: "ได้ ส่งภาพเช็กลิสต์หรือลิงก์หน้าเว็บของสถานทูตมาทาง LINE หรืออีเมล ทีมงานจะช่วยจัดลำดับขั้นให้ก่อนเริ่มงาน",
      },
    ],
    synonyms: ["sworn translator สถานทูต", "ผู้แปลที่สถานทูตยอมรับ", "แปลสาบานตนยื่นวีซ่า"],
  },
];

export const VISA_COMPARE_BY_SLUG: Record<string, VisaCompare> = Object.fromEntries(
  VISA_COMPARE.map((c) => [c.slug, c]),
);
